/**
 * E-Kuitti -dashboard (e-tasku-tyylinen).
 *
 * Ominaisuudet:
 * - KPI: kuukauden määrä, brutto, ALV, kategoriajako
 * - Lisää kuitti: upload / kamera → AI-OCR → editoitava lomake → tallenna
 * - Lista tab-jaolla: Kuukausi / Kaikki / Arkisto / Jakolinkit
 * - Rivin toiminnot: kuva, muokkaus, PDF, sähköposti, poisto
 * - Auto-linkitys kirjanpitoon (luo purchase-kirjauksen)
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Receipt as ReceiptIcon, Camera, Upload, Sparkles, Loader2, FileText,
  Edit, Trash2, Mail, Download, Plus, Link2, Copy, Eye, X, Users,
} from "lucide-react";
import { toast } from "sonner";
import { copyToClipboard, downloadBlob } from "@/lib/browser";
import { ModuleSettingsButton } from "@/components/admin/FactoryResetDialog";
import ModuleStaffTab from "@/components/staff/ModuleStaffTab";

const eur = (v) =>
  new Intl.NumberFormat("fi-FI", { style: "currency", currency: "EUR" })
    .format(Number(v || 0));

const fmtDate = (d) => (d || "").slice(0, 10);

const today = () => new Date().toISOString().slice(0, 10);
const currentMonth = () => new Date().toISOString().slice(0, 7);

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function monthOptions() {
  const arr = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    arr.push({
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      label: d.toLocaleDateString("fi-FI", { month: "long", year: "numeric" }),
    });
  }
  return arr;
}

const initialForm = () => ({
  vendor: "",
  vendor_business_id: "",
  receipt_date: today(),
  receipt_number: "",
  gross_amount: "",
  net_amount: "",
  vat_amount: "",
  vat_rate: 24,
  payment_method: "card",
  category: "Muu",
  description: "",
  notes: "",
  image_path: "",
  image_mime: "",
});

function recalcVat(form, changed) {
  const gross = parseFloat(form.gross_amount) || 0;
  const net = parseFloat(form.net_amount) || 0;
  const rate = parseFloat(form.vat_rate) || 0;
  const next = { ...form };
  if (changed === "gross_amount" || changed === "vat_rate") {
    if (gross > 0 && rate >= 0) {
      const n = +(gross / (1 + rate / 100)).toFixed(2);
      next.net_amount = n;
      next.vat_amount = +(gross - n).toFixed(2);
    }
  } else if (changed === "net_amount") {
    if (net > 0 && rate >= 0) {
      const v = +((net * rate) / 100).toFixed(2);
      next.vat_amount = v;
      next.gross_amount = +(net + v).toFixed(2);
    }
  }
  return next;
}

export default function EReceiptsDashboard() {
  const [categories, setCategories] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [stats, setStats] = useState(null);
  const [archive, setArchive] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [month, setMonth] = useState(currentMonth());
  const [tab, setTab] = useState("month");
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [createdLink, setCreatedLink] = useState(null);
  const [showImage, setShowImage] = useState(null);
  const [form, setForm] = useState(initialForm());
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [imageMime, setImageMime] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cats, list, st, mo] = await Promise.all([
        api.get("/ereceipts/categories"),
        api.get(`/ereceipts?month=${month}`),
        api.get(`/ereceipts/stats?month=${month}`),
        api.get(`/ereceipts/monthly`),
      ]);
      setCategories(cats.data);
      setReceipts(list.data);
      setStats(st.data);
      setArchive(mo.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  }, [month]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const loadTokens = async () => {
    try {
      const r = await api.get("/ereceipts/share-tokens/list");
      setTokens(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    if (tab === "share") loadTokens();
  }, [tab]);

  const handleFile = async (file) => {
    if (!file) return;
    const max = 10 * 1024 * 1024;
    if (file.size > max) {
      toast.error("Kuva on liian iso (max 10 MB)");
      return;
    }
    const b64 = await fileToBase64(file);
    setImagePreview(b64);
    setImageBase64(b64);
    setImageMime(file.type || "image/jpeg");
  };

  const runOcr = async () => {
    if (!imageBase64) return;
    setOcrLoading(true);
    try {
      const r = await api.post("/ereceipts/ocr", {
        image_base64: imageBase64,
        mime: imageMime,
      });
      const o = r.data;
      setForm((f) => ({
        ...f,
        vendor: o.vendor || f.vendor,
        vendor_business_id: o.vendor_business_id || f.vendor_business_id,
        receipt_date: o.receipt_date || f.receipt_date,
        receipt_number: o.receipt_number || f.receipt_number,
        gross_amount: o.gross_amount || f.gross_amount,
        net_amount: o.net_amount || f.net_amount,
        vat_amount: o.vat_amount || f.vat_amount,
        vat_rate: o.vat_rate ?? f.vat_rate,
        payment_method: o.payment_method || f.payment_method,
        category: o.suggested_category || f.category,
        description: o.notes || f.description,
      }));
      toast.success(`OCR valmis (luotettavuus ${Math.round((o.confidence || 0) * 100)} %)`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setOcrLoading(false);
  };

  const openNew = () => {
    setForm(initialForm());
    setImagePreview(null);
    setImageBase64(null);
    setImageMime(null);
    setShowForm(true);
  };

  const openEdit = (r) => {
    setForm({
      ...r,
      gross_amount: r.gross_amount ?? "",
      net_amount: r.net_amount ?? "",
      vat_amount: r.vat_amount ?? "",
    });
    setImagePreview(null);
    setImageBase64(null);
    setShowForm(true);
  };

  const saveReceipt = async () => {
    setSaving(true);
    try {
      let imagePath = form.image_path;
      let imageMimeFinal = form.image_mime;
      if (imageBase64 && !form.id) {
        // upload image
        const up = await api.post("/ereceipts/upload", {
          image_base64: imageBase64,
          mime: imageMime,
        });
        imagePath = up.data.path;
        imageMimeFinal = up.data.mime;
      }
      const payload = {
        ...form,
        gross_amount: parseFloat(form.gross_amount) || 0,
        net_amount: parseFloat(form.net_amount) || 0,
        vat_amount: parseFloat(form.vat_amount) || 0,
        vat_rate: parseFloat(form.vat_rate) || 0,
        image_path: imagePath || null,
        image_mime: imageMimeFinal || null,
      };
      delete payload.id;
      delete payload.owner_id;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.month;
      delete payload.status;
      payload.status = form.status || "new";
      if (form.id) {
        await api.put(`/ereceipts/${form.id}`, payload);
        toast.success("Kuitti päivitetty");
      } else {
        await api.post("/ereceipts", payload);
        toast.success("Kuitti tallennettu (kirjanpitoon synkronoitu)");
      }
      setShowForm(false);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSaving(false);
  };

  const deleteReceipt = async (id) => {
    if (!window.confirm("Poistetaanko kuitti?")) return;
    try {
      await api.delete(`/ereceipts/${id}`);
      toast.success("Poistettu");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadPdf = async (r) => {
    try {
      const res = await api.get(`/ereceipts/${r.id}/pdf`, { responseType: "blob" });
      downloadBlob(res.data, `kuitti-${r.receipt_date}.pdf`);
      toast.success("PDF ladattu");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendEmail = async (r) => {
    const to = window.prompt("Vastaanottajan sähköposti:", "");
    if (!to) return;
    try {
      const res = await api.post(`/ereceipts/${r.id}/email`, { to });
      if (res.data.status === "mock") {
        toast.warning("Sähköposti kirjattu (SMTP ei vielä konffattu)");
      } else {
        toast.success(`Sähköposti lähetetty: ${to}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const viewImage = async (r) => {
    if (!r.image_path) {
      toast.info("Tällä kuitilla ei ole liitettä");
      return;
    }
    try {
      const res = await api.get(`/ereceipts/image/${r.image_path}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setShowImage({ url, name: r.vendor || r.receipt_date });
    } catch (e) {
      toast.error("Kuvaa ei pystytty hakemaan");
    }
  };

  const createShareToken = async (label) => {
    try {
      const r = await api.post("/ereceipts/share-tokens", { label, expires_in_days: 30 });
      const url = `${window.location.origin}/scan/${r.data.token}`;
      const ok = await copyToClipboard(url);
      setCreatedLink({ url, label: r.data.label || label });
      if (ok) {
        toast.success("Linkki luotu ja kopioitu leikepöydälle");
      } else {
        toast.success("Linkki luotu — kopioi se modaalista");
      }
      loadTokens();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const revokeToken = async (token) => {
    if (!window.confirm("Poistetaanko jakolinkki?")) return;
    try {
      await api.delete(`/ereceipts/share-tokens/${token}`);
      loadTokens();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const updateForm = (key, value) => {
    setForm((f) => {
      const next = { ...f, [key]: value };
      if (["gross_amount", "net_amount", "vat_rate"].includes(key)) {
        return recalcVat(next, key);
      }
      return next;
    });
  };

  // ---------------------------------------------------------------------
  return (
    <div className="space-y-6" data-testid="ereceipts-page">
      {/* Header */}
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
            <ReceiptIcon className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">Moduuli</p>
            <h1 className="text-3xl font-display gradient-text-luxe">E-Kuitti</h1>
            <p className="text-white/60 text-sm mt-1">
              AI-OCR kuittiarkisto, automaattinen kirjanpito-linkitys, jakolinkit.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <ModuleSettingsButton moduleKey="ereceipt" moduleLabel="E-Kuitti" onAfterReset={loadAll} />
          <Button
            variant="outline"
            onClick={() => setShowShare(true)}
            className="border-amber-500/30 text-amber-200 inline-flex items-center gap-2"
            data-testid="ereceipts-share-link-button"
          >
            <Link2 className="w-4 h-4" /> Jakolinkki
          </Button>
          <Button
            onClick={openNew}
            className="btn-luxe inline-flex items-center gap-2"
            data-testid="ereceipts-add"
          >
            <Plus className="w-4 h-4" /> Uusi kuitti
          </Button>
        </div>
      </header>

      {/* Month picker */}
      <div className="flex items-center gap-3 flex-wrap">
        <Label className="text-xs text-white/60">Kuukausi</Label>
        <select
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="bg-input/40 border border-white/10 rounded-lg px-3 py-1.5 text-sm"
          data-testid="ereceipts-month-select"
        >
          {monthOptions().map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
        {stats && (
          <p className="text-xs text-white/40">
            {stats.count} kuittia · {eur(stats.gross_total)} brutto · {eur(stats.vat_total)} ALV
          </p>
        )}
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiBlock icon={ReceiptIcon} label="Kuitit" value={stats?.count ?? 0} accent="text-white" testid="ereceipts-kpi-count" />
        <KpiBlock icon={FileText} label="Brutto" value={eur(stats?.gross_total)} accent="text-amber-300" testid="ereceipts-kpi-gross" />
        <KpiBlock icon={Sparkles} label="Veroton" value={eur(stats?.net_total)} accent="text-emerald-300" testid="ereceipts-kpi-net" />
        <KpiBlock icon={Sparkles} label="ALV" value={eur(stats?.vat_total)} accent="text-violet-300" testid="ereceipts-kpi-vat" />
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white/[0.04] border border-white/5">
          <TabsTrigger value="month" data-testid="ereceipts-tab-month">Kuukausi</TabsTrigger>
          <TabsTrigger value="archive" data-testid="ereceipts-tab-archive">Arkisto</TabsTrigger>
          <TabsTrigger value="categories" data-testid="ereceipts-tab-categories">Kategoriat</TabsTrigger>
          <TabsTrigger value="share" data-testid="ereceipts-tab-share">Jakolinkit</TabsTrigger>
          <TabsTrigger value="staff" data-testid="ereceipts-tab-staff"><Users className="w-3.5 h-3.5 mr-1.5" />Henkilökunta</TabsTrigger>
        </TabsList>

        <TabsContent value="month" className="mt-4">
          <ReceiptList
            rows={receipts}
            loading={loading}
            onEdit={openEdit}
            onDelete={deleteReceipt}
            onPdf={downloadPdf}
            onEmail={sendEmail}
            onImage={viewImage}
          />
        </TabsContent>

        <TabsContent value="archive" className="mt-4">
          <ArchiveTable rows={archive?.months || []} onPick={(m) => { setMonth(m); setTab("month"); }} />
        </TabsContent>

        <TabsContent value="categories" className="mt-4">
          <CategoryBreakdown rows={stats?.by_category || []} />
        </TabsContent>

        <TabsContent value="share" className="mt-4">
          <ShareTokensView
            tokens={tokens}
            onCreate={createShareToken}
            onRevoke={revokeToken}
          />
        </TabsContent>

        <TabsContent value="staff" className="mt-4">
          <ModuleStaffTab moduleKey="ereceipt" moduleLabel="E-Kuitti" />
        </TabsContent>
      </Tabs>

      {/* ====================== ADD/EDIT DIALOG ====================== */}
      <Dialog open={showForm} onOpenChange={(v) => !v && setShowForm(false)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {form.id ? "Muokkaa kuittia" : "Uusi e-kuitti"}
            </DialogTitle>
            <DialogDescription>
              Skannaa kuittikuva (kamera / lataus) → AI parsii tiedot → vahvista ja tallenna.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Vasen: kuva + OCR */}
            <div className="space-y-3">
              <Label className="text-xs">Kuitin kuva</Label>
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 min-h-[260px] flex flex-col items-center justify-center text-center">
                {imagePreview ? (
                  <>
                    <img
                      src={imagePreview}
                      alt="Kuitti"
                      className="max-h-72 rounded-lg shadow-lg"
                      data-testid="ereceipts-image-preview"
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      className="mt-2 text-red-300"
                      onClick={() => { setImagePreview(null); setImageBase64(null); }}
                    >
                      <X className="w-3 h-3 mr-1" /> Poista kuva
                    </Button>
                  </>
                ) : (
                  <div className="space-y-3">
                    <Camera className="w-10 h-10 mx-auto text-white/30" />
                    <p className="text-sm text-white/60">Lataa kuva kuitista, tai ota kamerakuva</p>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  className="hidden"
                  data-testid="ereceipts-file-input"
                />
                <input
                  ref={cameraRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  className="hidden"
                />
                <Button variant="outline" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2">
                  <Upload className="w-4 h-4" /> Lataa
                </Button>
                <Button variant="outline" onClick={() => cameraRef.current?.click()} className="inline-flex items-center gap-2">
                  <Camera className="w-4 h-4" /> Kamera
                </Button>
              </div>
              <Button
                onClick={runOcr}
                disabled={!imageBase64 || ocrLoading}
                className="btn-luxe w-full inline-flex items-center gap-2"
                data-testid="ereceipts-run-ocr"
              >
                {ocrLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {ocrLoading ? "Lukee kuittia..." : "Lue tiedot kuvasta (AI)"}
              </Button>
            </div>

            {/* Oikea: lomake */}
            <div className="space-y-3">
              <FormField label="Myyjä" value={form.vendor} onChange={(v) => updateForm("vendor", v)} testid="ereceipts-field-vendor" />
              <FormField label="Y-tunnus" value={form.vendor_business_id} onChange={(v) => updateForm("vendor_business_id", v)} testid="ereceipts-field-vendor-id" />
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Päivä" type="date" value={form.receipt_date} onChange={(v) => updateForm("receipt_date", v)} testid="ereceipts-field-date" />
                <FormField label="Kuitti #" value={form.receipt_number} onChange={(v) => updateForm("receipt_number", v)} testid="ereceipts-field-number" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <FormField label="Brutto €" type="number" value={form.gross_amount} onChange={(v) => updateForm("gross_amount", v)} testid="ereceipts-field-gross" />
                <FormField label="Veroton €" type="number" value={form.net_amount} onChange={(v) => updateForm("net_amount", v)} testid="ereceipts-field-net" />
                <FormField label="ALV €" type="number" value={form.vat_amount} onChange={(v) => updateForm("vat_amount", v)} testid="ereceipts-field-vat" readOnly />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">ALV %</Label>
                  <select
                    value={form.vat_rate}
                    onChange={(e) => updateForm("vat_rate", e.target.value)}
                    className="w-full mt-1 bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
                    data-testid="ereceipts-field-vat-rate"
                  >
                    {[0, 10, 14, 24, 25.5].map((r) => (
                      <option key={r} value={r}>{r} %</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Maksutapa</Label>
                  <select
                    value={form.payment_method}
                    onChange={(e) => updateForm("payment_method", e.target.value)}
                    className="w-full mt-1 bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
                    data-testid="ereceipts-field-payment"
                  >
                    <option value="card">Kortti</option>
                    <option value="cash">Käteinen</option>
                    <option value="invoice">Lasku</option>
                    <option value="other">Muu</option>
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Kategoria</Label>
                <select
                  value={form.category}
                  onChange={(e) => updateForm("category", e.target.value)}
                  className="w-full mt-1 bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
                  data-testid="ereceipts-field-category"
                >
                  {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Selite</Label>
                <textarea
                  value={form.description}
                  onChange={(e) => updateForm("description", e.target.value)}
                  className="w-full mt-1 bg-input/40 border border-white/10 rounded-lg px-3 py-2 text-sm min-h-[80px]"
                  placeholder="Esim. lounas asiakaspalaverissa 3 hengelle"
                  data-testid="ereceipts-field-description"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button variant="ghost" onClick={() => setShowForm(false)} className="flex-1">
                  Peruuta
                </Button>
                <Button
                  onClick={saveReceipt}
                  disabled={saving}
                  className="btn-luxe flex-1"
                  data-testid="ereceipts-save"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Tallenna
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ====================== SHARE LINK DIALOG ====================== */}
      <Dialog open={showShare} onOpenChange={setShowShare}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Luo jakolinkki</DialogTitle>
            <DialogDescription>
              Linkin saaja voi avata kuitin lähetyssivun selaimessa, ottaa kuvan, AI parsii tiedot ja kuitti tallentuu suoraan sinun e-kuitti -moduuliisi.
            </DialogDescription>
          </DialogHeader>
          <ShareCreateForm onCreate={(label) => { createShareToken(label); setShowShare(false); }} />
        </DialogContent>
      </Dialog>

      {/* ====================== LUOTU LINKKI -MODAALI (clipboard-fallback) ====================== */}
      <Dialog open={!!createdLink} onOpenChange={(v) => !v && setCreatedLink(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Jakolinkki valmis</DialogTitle>
            <DialogDescription>
              {createdLink?.label ? `Linkki "${createdLink.label}" on käyttövalmis.` : "Linkki on käyttövalmis."}
              Kopioi alta ja lähetä työntekijälle / kumppanille.
            </DialogDescription>
          </DialogHeader>
          {createdLink && (
            <div className="space-y-3">
              <div className="flex gap-2">
                <Input
                  value={createdLink.url}
                  readOnly
                  className="flex-1 font-mono text-xs"
                  onClick={(e) => e.target.select()}
                  data-testid="ereceipts-created-link-input"
                />
                <Button
                  onClick={async () => {
                    const ok = await copyToClipboard(createdLink.url);
                    toast[ok ? "success" : "error"](ok ? "Kopioitu" : "Kopiointi estetty selaimessa — kopioi käsin");
                  }}
                  className="btn-luxe"
                  data-testid="ereceipts-created-link-copy"
                >
                  <Copy className="w-4 h-4 mr-1" /> Kopioi
                </Button>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => window.open(createdLink.url, "_blank", "noopener")}
                  data-testid="ereceipts-created-link-open"
                >
                  Avaa uudessa ikkunassa
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    const mailto = `mailto:?subject=${encodeURIComponent("E-Kuitti -jakolinkki")}&body=${encodeURIComponent(`Hei,\n\nVoit lähettää kuitteja tämän linkin kautta:\n${createdLink.url}\n\nApps2Go Hub`)}`;
                    window.location.href = mailto;
                  }}
                  data-testid="ereceipts-created-link-email"
                >
                  Lähetä sähköpostilla
                </Button>
              </div>
              <p className="text-[10px] text-white/40 text-center">
                Linkki vanhenee 30 vuorokauden kuluttua.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ====================== IMAGE VIEWER ====================== */}
      <Dialog open={!!showImage} onOpenChange={(v) => !v && setShowImage(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{showImage?.name || "Kuva"}</DialogTitle>
          </DialogHeader>
          {showImage && <img src={showImage.url} alt="Kuitti" className="w-full rounded-lg" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers / subcomponents
// ---------------------------------------------------------------------------
function KpiBlock({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="stat-block" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-widest text-white/40">{label}</p>
        <Icon className={`w-4 h-4 ${accent}`} />
      </div>
      <p className={`text-2xl font-display font-bold mt-1 ${accent}`}>{value}</p>
    </div>
  );
}

function FormField({ label, value, onChange, type = "text", testid, readOnly = false }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        step={type === "number" ? "0.01" : undefined}
        readOnly={readOnly}
        className={`mt-1 ${readOnly ? "opacity-60" : ""}`}
        data-testid={testid}
      />
    </div>
  );
}

function ReceiptList({ rows, loading, onEdit, onDelete, onPdf, onEmail, onImage }) {
  if (loading) {
    return (
      <div className="glass-card p-8 flex items-center gap-2 text-white/40">
        <Loader2 className="w-5 h-5 animate-spin" /> Ladataan...
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="glass-card p-12 text-center text-white/40">
        <ReceiptIcon className="w-10 h-10 mx-auto mb-3 text-white/20" />
        <p>Ei kuitteja tällä kuukaudella. Lisää ensimmäinen.</p>
      </div>
    );
  }
  return (
    <div className="glass-card overflow-hidden p-0">
      <table className="w-full text-sm relative z-10">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-widest text-white/40 border-b border-white/5">
            <th className="py-3 px-4">Pvm</th>
            <th className="py-3 px-4">Myyjä</th>
            <th className="py-3 px-4">Kategoria</th>
            <th className="py-3 px-4 text-right">Veroton</th>
            <th className="py-3 px-4 text-right">ALV</th>
            <th className="py-3 px-4 text-right">Brutto</th>
            <th className="py-3 px-4 w-44"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`ereceipts-row-${r.id}`}>
              <td className="py-3 px-4 text-white/80">{fmtDate(r.receipt_date)}</td>
              <td className="py-3 px-4">
                <p className="text-white font-medium">{r.vendor || "—"}</p>
                {r.description && <p className="text-[11px] text-white/40 mt-0.5">{r.description}</p>}
              </td>
              <td className="py-3 px-4">
                <Badge className="text-[10px] bg-amber-500/15 text-amber-200 border border-amber-500/30">
                  {r.category || "Muu"}
                </Badge>
              </td>
              <td className="py-3 px-4 text-right text-white/70 font-mono">{eur(r.net_amount)}</td>
              <td className="py-3 px-4 text-right text-violet-300 font-mono">{eur(r.vat_amount)}</td>
              <td className="py-3 px-4 text-right text-amber-300 font-mono font-semibold">{eur(r.gross_amount)}</td>
              <td className="py-3 px-4">
                <div className="flex gap-1 justify-end">
                  {r.image_path && (
                    <Button size="icon" variant="ghost" className="w-7 h-7" onClick={() => onImage(r)} data-testid={`ereceipts-view-${r.id}`}>
                      <Eye className="w-3 h-3" />
                    </Button>
                  )}
                  <Button size="icon" variant="ghost" className="w-7 h-7" onClick={() => onPdf(r)} data-testid={`ereceipts-pdf-${r.id}`}>
                    <Download className="w-3 h-3" />
                  </Button>
                  <Button size="icon" variant="ghost" className="w-7 h-7" onClick={() => onEmail(r)} data-testid={`ereceipts-email-${r.id}`}>
                    <Mail className="w-3 h-3" />
                  </Button>
                  <Button size="icon" variant="ghost" className="w-7 h-7" onClick={() => onEdit(r)} data-testid={`ereceipts-edit-${r.id}`}>
                    <Edit className="w-3 h-3" />
                  </Button>
                  <Button size="icon" variant="ghost" className="w-7 h-7 text-red-300" onClick={() => onDelete(r.id)} data-testid={`ereceipts-delete-${r.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ArchiveTable({ rows, onPick }) {
  if (rows.length === 0) {
    return <div className="glass-card p-8 text-center text-white/40">Ei kuukausiarkistoa tältä vuodelta.</div>;
  }
  return (
    <div className="glass-card overflow-hidden p-0">
      <table className="w-full text-sm relative z-10">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-widest text-white/40 border-b border-white/5">
            <th className="py-3 px-4">Kuukausi</th>
            <th className="py-3 px-4 text-right">Kuitteja</th>
            <th className="py-3 px-4 text-right">Veroton</th>
            <th className="py-3 px-4 text-right">ALV</th>
            <th className="py-3 px-4 text-right">Brutto</th>
            <th className="py-3 px-4"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.month} className="border-b border-white/5 hover:bg-white/[0.02]">
              <td className="py-3 px-4 text-white">{r.month}</td>
              <td className="py-3 px-4 text-right">{r.count}</td>
              <td className="py-3 px-4 text-right text-white/70 font-mono">{eur(r.net)}</td>
              <td className="py-3 px-4 text-right text-violet-300 font-mono">{eur(r.vat)}</td>
              <td className="py-3 px-4 text-right text-amber-300 font-mono font-semibold">{eur(r.gross)}</td>
              <td className="py-3 px-4">
                <Button size="sm" variant="ghost" onClick={() => onPick(r.month)}>Avaa</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CategoryBreakdown({ rows }) {
  if (rows.length === 0) {
    return <div className="glass-card p-8 text-center text-white/40">Ei dataa tälle kuukaudelle.</div>;
  }
  const max = Math.max(...rows.map((r) => r.amount), 1);
  return (
    <div className="glass-card p-6">
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.category}>
            <div className="flex items-center justify-between text-sm">
              <span className="text-white/80">{r.category}</span>
              <span className="text-amber-300 font-mono">{eur(r.amount)} <span className="text-white/40 text-xs">· {r.count} kpl</span></span>
            </div>
            <div className="w-full bg-white/5 rounded-full h-2 mt-1">
              <div
                className="bg-gradient-to-r from-amber-400 to-orange-500 h-2 rounded-full"
                style={{ width: `${(r.amount / max) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ShareCreateForm({ onCreate }) {
  const [label, setLabel] = useState("");
  return (
    <div className="space-y-3">
      <FormField label="Linkin nimi (näkyvyys)" value={label} onChange={setLabel} testid="ereceipts-share-label" />
      <Button onClick={() => onCreate(label)} className="btn-luxe w-full" data-testid="ereceipts-share-create">
        Luo linkki
      </Button>
    </div>
  );
}

function ShareTokensView({ tokens, onCreate, onRevoke }) {
  const [label, setLabel] = useState("");
  return (
    <div className="space-y-4">
      <div className="glass-card p-4 flex items-center gap-3 flex-wrap">
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Linkin nimi (esim. Toimisto)"
          className="flex-1 min-w-[180px]"
          data-testid="ereceipts-share-quick-label"
        />
        <Button onClick={() => { onCreate(label); setLabel(""); }} className="btn-luxe" data-testid="ereceipts-share-quick-create">
          <Plus className="w-4 h-4 mr-1" /> Uusi linkki
        </Button>
      </div>

      {tokens.length === 0 ? (
        <div className="glass-card p-8 text-center text-white/40">Ei vielä jakolinkkejä.</div>
      ) : (
        <div className="glass-card overflow-hidden p-0">
          <table className="w-full text-sm relative z-10">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-white/40 border-b border-white/5">
                <th className="py-3 px-4">Nimi</th>
                <th className="py-3 px-4">Linkki</th>
                <th className="py-3 px-4 text-right">Käyttökerrat</th>
                <th className="py-3 px-4">Vanhenee</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((t) => {
                const url = `${window.location.origin}/scan/${t.token}`;
                return (
                  <tr key={t.token} className="border-b border-white/5">
                    <td className="py-3 px-4 text-white/80">{t.label || "—"}</td>
                    <td className="py-3 px-4 text-white/60 truncate max-w-xs">
                      <code className="text-xs">{url}</code>
                    </td>
                    <td className="py-3 px-4 text-right">{t.uses_count}</td>
                    <td className="py-3 px-4 text-white/60">{(t.expires_at || "").slice(0, 10)}</td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1 justify-end">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="w-7 h-7"
                          onClick={async () => {
                            const ok = await copyToClipboard(url);
                            toast[ok ? "success" : "error"](ok ? "Kopioitu" : "Kopiointi estetty — paina F12 ja kopioi käsin");
                          }}
                          data-testid={`ereceipts-token-copy-${t.token}`}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="w-7 h-7 text-red-300"
                          onClick={() => onRevoke(t.token)}
                          data-testid={`ereceipts-token-revoke-${t.token}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
